import * as S from "./planCard.style";
import FlexContainer from "~/spocketUI/components/FlexContainer";
import Button from "~/spocketUI/components/Button";
import Separator from "~/spocketUI/components/Separator";
import { SVG } from "~/spocketUI/components/SVG";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { SVGIcon } from "~/spocketUI/components/SVG/types";
import { Trans, useTranslation } from "react-i18next";
import { IPlan, SubscriptionType } from "~/types/billing";
import { centsToDecimal, getTotalPlanCost } from "~/spocketUI/helpers/numbers";
import { useNavigate } from "react-router-dom";
import { IStore } from "~/types/store";
import { useMemo } from "react";

type Props = {
  plan?: IPlan;
  store: IStore | null;
  closeModal?: () => void;
};

const PlanCard = ({ plan, store, closeModal }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!plan) return null;

  const isAnnual = plan.interval === SubscriptionType.ANNUAL;
  const bgColor = plan.is_highlighted ? "primary" : "transparent";
  const textColor = plan.is_highlighted ? "white" : "text";
  const isSubscribed = plan.id === store?.active_subscription?.plan?.id;
  const buttonText = useMemo(() => {
    if (isSubscribed) return t("settings.current");
    if (isAnnual || store?.has_subscribed_before) return t("settings.start");

    return t("settings.try_for_free");
  }, [isAnnual, isSubscribed, store?.active_subscription?.plan]);

  const handleRedirect = () => {
    localStorage.setItem("plansModal", "false");
    navigate(`/checkout/${plan.id}`);
    closeModal && closeModal();
  };

  return (
    <S.PlanCardContainer primary={plan.is_highlighted} data-testid="plan-card">
      {isAnnual || plan.is_highlighted ? (
        <S.PlanCardPromotion isHighlighted={plan.is_highlighted}>
          {isAnnual ? (
            <Trans
              i18nKey="settings.months_off"
              values={{ days: plan.months_off }}
            />
          ) : (
            plan.is_highlighted && (
              <S.MostPopularText>🔥 {t("settings.popular")}</S.MostPopularText>
            )
          )}
        </S.PlanCardPromotion>
      ) : null}

      <FlexContainer
        width="100%"
        alignItems="flex-start"
        gap="0"
        flexDirection="column"
      >
        <S.PlanCardTitle>{plan.name}</S.PlanCardTitle>
        <S.PlanCardPrice>
          {plan.old_cost_per_month && (
            <S.DiscountText>
              ${centsToDecimal(plan.old_cost_per_month)}
            </S.DiscountText>
          )}
          <Trans
            i18nKey="settings.mon"
            components={{ 1: <S.MonthlyValue /> }}
            values={{
              value: Math.round(centsToDecimal(plan.cost_per_month)),
            }}
          />
        </S.PlanCardPrice>
        <S.PlanCardSubtitle>
          <Trans
            i18nKey={isAnnual ? "settings.billed_annually" : "settings.trial"}
            values={{
              amount: `$${Math.round(getTotalPlanCost(plan))}`,
            }}
          />
        </S.PlanCardSubtitle>
      </FlexContainer>
      <Separator type="horizontal" />
      <S.ButtonContainer>
        <Button
          size="xl"
          long
          bgColor={bgColor}
          width="100%"
          color={textColor}
          outline={!plan.is_highlighted}
          fontWeight={600}
          isDisabled={!!isSubscribed}
          onClick={handleRedirect}
        >
          {buttonText}
        </Button>
      </S.ButtonContainer>
      <S.PlanCardList data-testid="plan-features">
        {plan.features.map((feature: string, i: number) => (
          <S.PlanCardItem key={`${feature}-${i}`}>
            <SVG icon={faCheck as SVGIcon} color="green" />
            <S.FeatureText dangerouslySetInnerHTML={{ __html: feature }} />
          </S.PlanCardItem>
        ))}
      </S.PlanCardList>
    </S.PlanCardContainer>
  );
};

export default PlanCard;
